import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../../components/button/PrimaryButton";
import { isEmpty } from "../../helpers/validator";
import { createSuperAdminTenant } from "../../services/superadmin-tenants";
import TenantConfigFields, {
  compactTenantConfig,
  createEmptyTenantConfig,
} from "./TenantConfigFields";

const TenantCreate = () => {
  const [tenant, setTenant] = useState({ code: "", name: "", active: true });
  const [admin, setAdmin] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });
  const [config, setConfig] = useState(createEmptyTenantConfig());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const navigate = useNavigate();

  const updateTenant = (key, value) =>
    setTenant((prev) => ({ ...prev, [key]: value }));
  const updateAdmin = (key, value) =>
    setAdmin((prev) => ({ ...prev, [key]: value }));
  const updateConfig = (key, value) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    const nextErrors = [];
    if (isEmpty(tenant.code)) nextErrors.push("Tenant code is required.");
    if (isEmpty(tenant.name)) nextErrors.push("Tenant name is required.");
    if (isEmpty(admin.email)) nextErrors.push("Admin email is required.");
    if (isEmpty(admin.password)) nextErrors.push("Admin password is required.");
    if (isEmpty(admin.firstName))
      nextErrors.push("Admin first name is required.");
    if (isEmpty(admin.lastName))
      nextErrors.push("Admin last name is required.");
    return nextErrors;
  };

  const handleSave = async () => {
    const nextErrors = validate();
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    setErrors([]);
    try {
      const response = await createSuperAdminTenant({
        tenant,
        admin,
        config: compactTenantConfig(config),
      });
      navigate(`/su/admin/tenants/${response.code ?? tenant.code}`, {
        replace: true,
      });
    } catch (e) {
      setErrors([e.message]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded shadow p-6">
      <h1 className="uppercase text-xl font-bold text-slate-700 mb-6">
        Create Tenant
      </h1>
      {errors.length > 0 ? (
        <div className="bg-errorBg text-errorText text-sm p-4 mb-6 border-errorBorder border-[0.5px] rounded-sm">
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Input
          label="Tenant Code"
          value={tenant.code}
          onChange={(value) => updateTenant("code", value)}
        />
        <Input
          label="Tenant Name"
          value={tenant.name}
          onChange={(value) => updateTenant("name", value)}
        />
        <label className="flex items-center gap-2 mt-7 text-sm font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={tenant.active}
            onChange={(e) => updateTenant("active", e.target.checked)}
          />
          Active
        </label>
      </div>

      <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
        Initial Admin
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Input
          label="Email"
          type="email"
          value={admin.email}
          onChange={(value) => updateAdmin("email", value)}
        />
        <Input
          label="Password"
          type="password"
          value={admin.password}
          onChange={(value) => updateAdmin("password", value)}
        />
        <Input
          label="First Name"
          value={admin.firstName}
          onChange={(value) => updateAdmin("firstName", value)}
        />
        <Input
          label="Last Name"
          value={admin.lastName}
          onChange={(value) => updateAdmin("lastName", value)}
        />
      </div>

      <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
        Tenant Config
      </h2>
      <TenantConfigFields config={config} onChange={updateConfig} />

      <div className="flex justify-end mt-8">
        <PrimaryButton
          title="Create Tenant"
          onClick={handleSave}
          isBusy={saving}
        />
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="block uppercase text-slate-600 text-xs font-bold mb-2">
      {label}
    </label>
    <input
      type={type}
      aria-label={label}
      className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-neutral-light rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default TenantCreate;
